import React from 'react';
import title from 'title';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { ViewCommunityDropdown } from '@components/basics/Dropdowns/ViewCommunityDropdown';
import { SeeAllLink } from '@components/basics/SeeAll';
import { Chip } from '@components/main-components/elements/chips/Chip';
import { useAuth } from '@context/auth/base';
import type { ArtistCollectorsPreviewQueryVariables, FragmentType } from '@graphql/reactQuery';
import { getFragment, prefetchQuery } from '@graphql/reactQuery';
import { useQuery } from '@graphql/reactQuery';
import { ArtistCollectorsPreviewDocument, CollectorSectionFragmentDoc } from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogError';
import { CollectorsSection, NUM_BACKED_ENTITIES_PREVIEW } from './CollectorsSection';

export const CollectorsSort = {
  TOP: 'top',
  EARLIEST: 'earliest',
} as const;

export type CollectorsSort = (typeof CollectorsSort)[keyof typeof CollectorsSort];

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment CollectorSection on Artist {
    id
    user {
      id
      ...SeeAllUserInfo
    }
  }

  query ArtistCollectorsPreview(
    $artistId: UUID!
    $sort: ArtistCollectorCursorConnectionSort!
    $first: NonNegativeInt!
  ) {
    artist(id: $artistId) {
      id
      collectors(pagination: { first: $first, sort: $sort }) {
        pageInfo {
          hasNextPage
        }
        edges {
          node {
            id
            ...CollectorCardInfo
          }
        }
      }
    }
  }
`);

export const CollectorsSortChips = ({
  sort,
  setSort,
  onMouseOver,
}: {
  sort: CollectorsSort;
  setSort: (sort: CollectorsSort) => void;
  onMouseOver?: (sort: CollectorsSort) => void;
}) => {
  return (
    <Chips>
      <Chip
        text={title(CollectorsSort.TOP)}
        isSelected={sort === CollectorsSort.TOP}
        onClick={() => setSort(CollectorsSort.TOP)}
        onMouseOver={() => onMouseOver?.(CollectorsSort.TOP)}
      />
      <Chip
        text={title(CollectorsSort.EARLIEST)}
        isSelected={sort === CollectorsSort.EARLIEST}
        onClick={() => setSort(CollectorsSort.EARLIEST)}
        onMouseOver={() => onMouseOver?.(CollectorsSort.EARLIEST)}
      />
    </Chips>
  );
};

export const Collectors = ({ artist }: { artist: FragmentType<CollectorSectionFragmentDoc> }) => {
  const artistData = getFragment(CollectorSectionFragmentDoc, artist);
  const [sort, setSort] = React.useState<CollectorsSort>(CollectorsSort.TOP);
  const { authUser } = useAuth();
  const isOwner = authUser?.id === artistData?.user?.id;

  function artistCollectorsPreviewVariables(
    querySort: CollectorsSort,
  ): ArtistCollectorsPreviewQueryVariables {
    return {
      artistId: artistData.id,
      first: NUM_BACKED_ENTITIES_PREVIEW,
      sort:
        querySort === 'top'
          ? {
              nftsCount: 'DESC',
            }
          : {
              firstNftCollectedDate: 'ASC',
            },
    };
  }

  const { data, isLoading: loading } = useQuery(ArtistCollectorsPreviewDocument, {
    variables: artistCollectorsPreviewVariables(sort),
    staleTime: 0,
  });

  const collectorsData = React.useMemo(
    () => data?.data.artist?.collectors.edges.map(v => v.node),
    [data],
  );

  const empty = collectorsData && collectorsData.length === 0;

  const logError = useLogError();

  const showSeeAll = data?.data.artist?.collectors.pageInfo.hasNextPage || false;

  if ((empty && !loading) || !artistData) {
    return null;
  }

  return (
    <Container>
      <Heading>Collectors</Heading>
      <div className="mb-6 mt-2 flex w-full items-center justify-between lt:mb-8">
        <div className="flex items-center gap-2 ">
          <CollectorsSortChips
            sort={sort}
            setSort={setSort}
            onMouseOver={sort => {
              prefetchQuery(ArtistCollectorsPreviewDocument, {
                variables: artistCollectorsPreviewVariables(sort),
                staleTime: Infinity,
              }).catch(error => {
                logError({
                  error,
                });
              });
            }}
          />
          {isOwner && <ViewCommunityDropdown />}
        </div>

        {showSeeAll && (
          <SeeAllLink
            user={artistData.user}
            path="/collectors"
            params={sort !== CollectorsSort.TOP ? { sort } : null}
          />
        )}
      </div>

      <CollectorsSection
        data={collectorsData}
        loading={loading && !data}
        sort={sort}
        variant="collector"
      />
    </Container>
  );
};

export const Container = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  background: '$white',
  marginTop: '$6',
  paddingX: '$5',

  '@laptop': {
    marginTop: '$12',
    paddingX: 0,
  },
});

export const Wrapper = styled('div', {
  display: 'flex',
  width: '100%',
  justifyContent: 'space-between',
  marginBottom: 32,

  '@laptop': {
    marginBottom: 24,
  },
});

const Chips = styled('div', {
  display: 'flex',
  gap: 8,
});

export const Heading = styled('h3', {
  title: 'M',
  '@tablet': {
    title: 'L',
  },
});
